import { Injectable } from '@angular/core';
import { Approval, PagedResult } from '@core/interfaces';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Equipment } from './../../../../core/interfaces/equipments/index';

export interface ApprovalAndEquipment {
  approval: Approval;
  equipment?: Equipment;
}

@Injectable({
  providedIn: 'root'
})
export class ApprovalsEquipmentsApi {

  constructor(private readonly apollo: Apollo) { }

  async getApprovalAndEquipment({ approvalId, equipmentQrCode }): Promise<ApprovalAndEquipment> {
    return this.apollo.query({
      query: gql`
      query approvalAndEquipment($approvalId: ID!, $equipmentQrCode: String!) {
        approval(id: $approvalId) {
          id
          createdAt
          status
          notes
          resolvedAt
          resolvedBy {
            id
            name
            email
          }
          equipment {
            id
            originalEquipmentId
            name
            qrCode
            number
            createdAt
            notes
            archived
            type {
              name
            }
            attachments {
              id
              path
              title
              createdAt
              archived
            }
            customFields {
              id
              name
              value
              type
              position
            }
            location {
              id
              name
              address {
                formattedAddress
              }
            },
            locationSector {
              name
            },
            locationEnvironment {
              name
            }
          }
          company {
            id
            name
            avatarUrl
            fieldAccountId
          }
        }

        equipments(where: { qrCode_contains: $equipmentQrCode }, limit: 1, offset: 0) {
          items {
            id
            imageUrl
            name
            number
            qrCode
            createdAt
            notes
            archived
            location {
              id
              name
              address {
                formattedAddress
              }
            }
            locationSector {
              name
            }
            locationEnvironment {
              name
            }
            type {
              id
              name
              segment {
                id
              }
            }
            attachments {
              id
              path
              title
              createdAt
              archived
            }
            customFields {
              id
              name
              value
              position
              type
            }
          }
        }
      }
    `,
      variables: {
        approvalId,
        equipmentQrCode
      }
    }).toPromise()
      .then(({ data }) => {
        return {
          approval: data['approval'],
          equipment: data['equipments']['items'][0]
        };
      });
  }

  listApprovalsEquipments({ where, orderBy, limit, offset }): Observable<PagedResult<Approval>> {
    return this.apollo
      .query({
        query: gql`
          query getApprovalsEquipments($where: ApprovalWhereInput, $orderBy: ApprovalOrderByInput, $limit: Int, $offset: Int) {
            approvals(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
              totalCount
              pageInfo {
                hasNextPage
                hasPreviousPage
              }
              items {
                id
                createdAt
                status
                notes
                resolvedAt
                resolvedBy {
                  id
                  name
                  avatarUrl
                }
                equipment {
                  originalEquipmentId
                  qrCode
                  number
                  name
                  createdAt
                  type {
                    name
                  }
                  location {
                    name
                  }
                }
                company {
                  name
                }
              }
            }
      }
        `,
        variables: {
          where,
          orderBy,
          limit,
          offset
        }
      })
      .pipe(map((response) => response.data['approvals'] as PagedResult<Approval>));
  }

  async searchEquipment(where): Promise<Equipment> {
    return this.apollo.query({
      query: gql`
      query searchEquipment($where: EquipmentWhereInput) {
        equipments(where: $where) {
          items {
            id
            number
            archived
            qrCode
          }
        }
      }
    `,
      variables: {
        where: where
      }
    }).toPromise()
      .then(({ data }) => {
        return data['equipments']['items'][0];
      });
  }
}
